/**
 * Field Type Registry Loader (Portal)
 *
 * Resolution order:
 *   1. In-memory cache (page lifetime)
 *   2. localStorage cache (across sessions, cleared on logout)
 *   3. Network fetch with If-None-Match (304 → keep persisted)
 *   4. Bundled fallback (committed snapshot, ensures first paint always works)
 */

import axiosInstance from '../api/axiosInstance';
import bundled from './fieldRegistry.bundled.json';

const STORAGE_KEY = 'aqrate:fieldRegistry:v1';
const ENDPOINT = '/api/formmetadata/field-registry';
const MAX_SUPPORTED_SCHEMA_VERSION = 1;

const BUNDLED = bundled;
let _memory = null;
let _refreshPromise = null;

function readPersisted() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isValidPayload(parsed?.payload)) return null;
    if (parsed.payload.schemaVersion > MAX_SUPPORTED_SCHEMA_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePersisted(cache) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Quota exceeded or storage unavailable — non-fatal
  }
}

function isValidPayload(p) {
  return (
    p &&
    typeof p === 'object' &&
    typeof p.version === 'string' &&
    typeof p.schemaVersion === 'number' &&
    Array.isArray(p.entries) &&
    p.entries.length > 0 &&
    Array.isArray(p.presentation)
  );
}

/** Synchronously return the best registry currently available. */
export function getRegistry() {
  if (_memory) return { payload: _memory.payload, source: 'memory' };
  const persisted = readPersisted();
  if (persisted) {
    _memory = persisted;
    return { payload: persisted.payload, source: 'persisted' };
  }
  return { payload: BUNDLED, source: 'bundled' };
}

/** Force a fetch from the API; updates memory + localStorage on success. */
export async function refresh() {
  if (_refreshPromise) return _refreshPromise;

  _refreshPromise = (async () => {
    const cached = _memory ?? readPersisted();
    try {
      const resp = await axiosInstance.get(ENDPOINT, {
        headers: cached?.etag ? { 'If-None-Match': cached.etag } : {},
        validateStatus: (s) => s === 200 || s === 304,
      });

      if (resp.status === 304) {
        if (cached) _memory = cached;
        return { updated: false, source: 'cache' };
      }

      if (!isValidPayload(resp.data) || resp.data.schemaVersion > MAX_SUPPORTED_SCHEMA_VERSION) {
        return { updated: false, source: 'cache' };
      }

      const next = {
        etag: resp.headers?.etag ?? null,
        fetchedAt: new Date().toISOString(),
        payload: resp.data,
      };
      _memory = next;
      writePersisted(next);
      return { updated: true, source: 'network' };
    } catch {
      return { updated: false, source: 'cache' };
    } finally {
      _refreshPromise = null;
    }
  })();

  return _refreshPromise;
}

/** Clear the cached registry — call on logout. */
export function clearRegistryCache() {
  _memory = null;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best effort
  }
}

export const BUNDLED_REGISTRY_VERSION = BUNDLED.version;
